import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { mapReviewError, reviewVisibilitySchema, useSetReviewVisibility } from '@dukanti/shared';
import type {
  AdminProductReviewRow,
  NormalizedReviewVisibilityValues,
  ReviewVisibilityFormValues,
} from '@dukanti/shared';
import { useTranslation } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const reviewVisibilityResolver = zodResolver(reviewVisibilitySchema) as Resolver<
  ReviewVisibilityFormValues,
  unknown,
  NormalizedReviewVisibilityValues
>;

interface ReviewVisibilityDialogProps {
  readonly review: AdminProductReviewRow | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function ReviewVisibilityDialog({
  review,
  open,
  onOpenChange,
}: ReviewVisibilityDialogProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const mutation = useSetReviewVisibility(supabase);
  const nextVisibility = review?.visibility === 'visible' ? 'hidden' : 'visible';
  const form = useForm<ReviewVisibilityFormValues, unknown, NormalizedReviewVisibilityValues>({
    resolver: reviewVisibilityResolver,
    defaultValues: { visibility: nextVisibility, hiddenReason: null, hiddenNote: '' },
  });
  const visibility = form.watch('visibility');

  useEffect(() => {
    if (open) form.reset({ visibility: nextVisibility, hiddenReason: null, hiddenNote: '' });
  }, [form, nextVisibility, open, review?.id]);

  const submit = form.handleSubmit((values) => {
    if (!review) return;
    mutation.mutate(
      {
        reviewId: review.id,
        visibility: values.visibility,
        hiddenReason: values.hiddenReason,
        hiddenNote: values.hiddenNote,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} data-testid="review-visibility-dialog">
        <DialogHeader>
          <DialogTitle>
            {nextVisibility === 'hidden'
              ? t('admin.reviews.moderation.hideTitle')
              : t('admin.reviews.moderation.unhideTitle')}
          </DialogTitle>
          <DialogDescription>{review?.product_name_snapshot ?? ''}</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          {mutation.error ? (
            <p className="text-destructive text-start text-sm">
              {t(mapReviewError(mutation.error))}
            </p>
          ) : null}
          {visibility === 'hidden' ? (
            <>
              <label className="block space-y-1.5 text-start">
                <span className="text-foreground text-sm font-medium">
                  {t('admin.reviews.moderation.reasonLabel')}
                </span>
                <span className="relative block">
                  <select
                    {...form.register('hiddenReason')}
                    className="border-input bg-input-background text-foreground focus-visible:ring-ring/50 h-10 w-full appearance-none rounded-lg border px-3 py-2 pe-10 text-start text-sm outline-none [-moz-appearance:none] [-webkit-appearance:none] focus-visible:ring"
                  >
                    <option value="">{t('admin.reviews.visibility.reasonRequired')}</option>
                    <option value="abusive">{t('admin.reviews.hideReasons.abusive')}</option>
                    <option value="private_info">
                      {t('admin.reviews.hideReasons.privateInfo')}
                    </option>
                    <option value="off_topic">{t('admin.reviews.hideReasons.offTopic')}</option>
                    <option value="spam">{t('admin.reviews.hideReasons.spam')}</option>
                    <option value="other">{t('admin.reviews.hideReasons.other')}</option>
                  </select>
                  <ChevronDown
                    className="text-muted-foreground pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2"
                    aria-hidden
                  />
                </span>
              </label>
              {form.formState.errors.hiddenReason?.message ? (
                <p className="text-destructive text-start text-sm">
                  {t(form.formState.errors.hiddenReason.message as never)}
                </p>
              ) : null}
              <label className="block space-y-1.5 text-start">
                <span className="text-foreground text-sm font-medium">
                  {t('admin.reviews.moderation.noteLabel')}
                </span>
                <Textarea
                  rows={3}
                  placeholder={t('admin.reviews.moderation.notePlaceholder')}
                  {...form.register('hiddenNote')}
                />
              </label>
            </>
          ) : null}
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              {nextVisibility === 'hidden'
                ? t('admin.reviews.actions.hide')
                : t('admin.reviews.actions.unhide')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
